import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import { CgMenuLeft } from "react-icons/cg";
import { FiSearch } from "react-icons/fi";
import { Tooltip } from "antd";
import Breadcrumbs from "../../components/Breadcrumbs";
import NoPermissionModal from "../../components/modals/NoPermissionModal";
import NewTableComponent from "../../components/NewTableComponent";
import TablePopover from "../../components/TablePopover";
import TripIdFilterDropdown from "../../components/TripIdFilterDropdown";
import VehicleFilterDropdown from "../../components/VehicleFilterDropdown";
import DateFilterDropdown from "../../components/DateFilterDropdown";
import { apiService } from "../../services/apiService";
import { fetchVehiclesDropdown } from "../../store/Slices/dropdownSlice";
import {
  formatDate,
  hasPermission,
  hasPettyCashPermission,
  hasTripPermission,
  isSuperAdminRole,
} from "../../utils/helper";
import { AppMainModules, TripPermission, TripSubModules } from "../../utils/app-constant";
import { errorNotify, successNotify } from "../../utils/toast";
import Notification from "../../components/Notification";
import ExportButton from "../../components/ExportButton";
import { approvedIcon, closedIcon } from "../../utils/svgs";
import MobileListingFilters from "../../components/MobileListingFilters";
import "../../styles/Trip.css";
import "../../styles/Access.css";
import "../../styles/TripsList.css";
import "../../styles/Services.css";

const renderStatusBadge = ({
  statusValue,
  canChangeStatus = false,
  isOpen = false,
  isUpdating = false,
  className = "",
}) => {
  const rawStatus = String(statusValue || "")
    .trim()
    .toLowerCase();

  const statusConfig = {
    pending: { label: "Pending", className: "status_pending" },
    approved: { label: "Approved", className: "status_approved" },
    rejected: { label: "Rejected", className: "status_rejected" },
  };

  const config = statusConfig[rawStatus] || {
    label: rawStatus
      ? rawStatus.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
      : "—",
    className: "status_default",
  };

  return (
    <span
      className={`petty_cash_status_badge ${config.className} ${canChangeStatus ? "is_interactive" : ""
        } ${isOpen ? "is_open" : ""} ${className}`}
    >
      {isUpdating ? (
        <span className="petty_cash_badge_spinner" aria-hidden="true" />
      ) : (
        <span className="petty_cash_badge_dot" aria-hidden="true" />
      )}
      <span className="petty_cash_badge_label">
        {isUpdating ? "Updating..." : config.label}
      </span>
      {canChangeStatus && !isUpdating && (
        <span className="petty_cash_badge_chevron" aria-hidden="true">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path
              d="M2.5 3.75L5 6.25L7.5 3.75"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}
    </span>
  );
};

const formatSource = (row) => {
  const source = row?.source;
  if (!source) return "-";
  if (source === "filling-station")
    return row?.filling_station?.name || "Filling Station";
  return source.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

const formatAmount = (amount) => {
  if (amount === null || amount === undefined || amount === "") return "-";
  const numericValue = Number(amount);
  if (!Number.isFinite(numericValue)) return String(amount);
  return numericValue.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const pettyCashStatusOptions = [
  { value: "approved", label: "Approved", icon: approvedIcon },
  { value: "rejected", label: "Rejected", icon: closedIcon },
];

const PettyCash = () => {
  const outletContext = useOutletContext() || {};
  const toggleMobileSidebar = outletContext?.toggleMobileSidebar;

  const canExportPettyCashPdf = useMemo(
    () =>
      isSuperAdminRole() ||
      hasPettyCashPermission("petty-cash-export-pdf") ||
      hasTripPermission(TripPermission.PettyCashExportPdf),
    [],
  );
  const canExportPettyCashExcel = useMemo(
    () =>
      isSuperAdminRole() ||
      hasPettyCashPermission("petty-cash-export-excel") ||
      hasTripPermission(TripPermission.PettyCashExportExcel),
    [],
  );
  const canUpdatePettyCashStatus = useMemo(
    () =>
      isSuperAdminRole() ||
      hasPettyCashPermission("petty-cash-approval-status") ||
      hasPettyCashPermission("status-update") ||
      hasPettyCashPermission("petty-cash-status-update") ||
      hasTripPermission("petty-cash-approval-status") ||
      hasTripPermission(TripPermission.PettyCashStatusUpdate) ||
      hasPermission({
        permissionSlug: "petty-cash-approval-status",
        menuSlug: TripSubModules.PettyCash,
        moduleSlug: AppMainModules.tripManagement,
      }) ||
      hasPermission({
        permissionSlug: TripPermission.PettyCashStatusUpdate,
        menuSlug: TripSubModules.PettyCash,
        moduleSlug: AppMainModules.tripManagement,
      }),
    [],
  );

  const dispatch = useDispatch();
  const { vehiclesDropdownData = [] } = useSelector(
    (state) => state.dropdowns || state.dropdown || {},
  );

  const [pettyCashList, setPettyCashList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [searchFilters, setSearchFilters] = useState({});
  const [paymentModeOptions, setPaymentModeOptions] = useState([]);
  const [pdfExportLoading, setPdfExportLoading] = useState(false);
  const [excelExportLoading, setExcelExportLoading] = useState(false);
  const [statusPopup, setStatusPopup] = useState({
    open: false,
    rowId: null,
    anchorPos: { x: 0, y: 0 },
  });
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [restrictedStatusInfo, setRestrictedStatusInfo] = useState(null);
  const statusPopupRef = useRef();

  const updateSearchFilters = useCallback((nextFilters) => {
    setCurrentPage(1);
    setSearchFilters(nextFilters);
  }, []);

  const handleSearchChange = useCallback((nextSearchText) => {
    setCurrentPage(1);
    setSearchText(nextSearchText);
  }, []);

  const hasActiveFilters = useMemo(() => {
    const hasSearchText = Boolean(
      searchText && String(searchText).trim().length > 0,
    );
    const hasSearchFilters = Boolean(
      searchFilters &&
      typeof searchFilters === "object" &&
      Object.keys(searchFilters).some((key) => {
        const filter = searchFilters[key];
        if (!filter) return false;
        if (Array.isArray(filter.value)) return filter.value.length > 0;
        return (
          filter.value !== undefined &&
          filter.value !== null &&
          filter.value !== ""
        );
      }),
    );
    return hasSearchText || hasSearchFilters;
  }, [searchText, searchFilters]);

  const buildExportPayload = useCallback(() => {
    const payload = {};
    if (searchText && String(searchText).trim()) {
      payload.search_text = String(searchText).trim();
    }
    if (searchFilters && typeof searchFilters === "object") {
      Object.assign(payload, searchFilters);
    }
    return payload;
  }, [searchText, searchFilters]);

  const handleExportPdf = async () => {
    if (!canExportPettyCashPdf || !hasActiveFilters || pdfExportLoading) return;
    setPdfExportLoading(true);
    try {
      const payload = buildExportPayload();
      const response = await apiService.getPettyCashListingPdf(payload);
      const responseData = response?.data ?? response;
      const filePath =
        responseData?.url ||
        responseData?.file ||
        (typeof responseData === "string" ? responseData : null);
      if (!filePath) {
        throw new Error("PDF was generated but no file path was returned");
      }
      const fileUrl = /^https?:\/\//i.test(filePath)
        ? filePath
        : `${process.env.REACT_APP_API_FILES_URL}${filePath}`;
      const link = document.createElement("a");
      link.href = fileUrl;
      link.target = "_blank";
      link.setAttribute("download", "petty-cash-listing.pdf");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      errorNotify(err?.message || err || "Failed to download Petty Cash PDF");
    } finally {
      setPdfExportLoading(false);
    }
  };

  const handleExportExcel = async () => {
    if (!canExportPettyCashExcel || !hasActiveFilters || excelExportLoading)
      return;
    setExcelExportLoading(true);
    try {
      const payload = buildExportPayload();
      const response = await apiService.getPettyCashListingExcel(payload);
      const responseData = response?.data ?? response;
      const filePath =
        responseData?.url ||
        responseData?.file ||
        (typeof responseData === "string" ? responseData : null);
      if (!filePath) {
        throw new Error("Excel was generated but no file path was returned");
      }
      const fileUrl = /^https?:\/\//i.test(filePath)
        ? filePath
        : `${process.env.REACT_APP_API_FILES_URL}${filePath}`;
      const link = document.createElement("a");
      link.href = fileUrl;
      link.target = "_blank";
      link.setAttribute("download", "petty-cash-listing.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      errorNotify(err?.message || err || "Failed to download Petty Cash Excel");
    } finally {
      setExcelExportLoading(false);
    }
  };

  useEffect(() => {
    dispatch(fetchVehiclesDropdown());
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      try {
        const response = await apiService.getPettyCashModes();
        const modes = response?.data || response?.results || [];
        const options = (Array.isArray(modes) ? modes : []).map((mode) => {
          if (typeof mode === "string") {
            return { label: mode, value: mode };
          }
          const label =
            mode?.title ||
            mode?.name ||
            mode?.label ||
            mode?.slug ||
            String(mode?.id || "");
          const value =
            mode?.slug || mode?.title || mode?.name || mode?.value || mode?.id;
          return { label, value };
        });
        setPaymentModeOptions(options);
      } catch (err) {
        // Non-critical: filter dropdown simply stays empty if this fails.
      }
    })();
  }, []);

  const vehicleDropdownOptions = useMemo(() => {
    return (vehiclesDropdownData || []).map((v) => ({
      label: v.registration_number || v.label || String(v.id || v.value),
      value: v.id ?? v.value,
    }));
  }, [vehiclesDropdownData]);

  const fetchPettyCash = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getPettyCashPaginated({
        page: currentPage,
        limit: limit,
        search_text: searchText,
        searchFilters: searchFilters,
      });

      const list = response?.data?.results || response?.data?.data || [];
      const meta = response?.data?.meta || {};
      const total = meta?.totalItems ?? response?.data?.total ?? list.length;
      const pages = meta?.totalPages ?? Math.max(1, Math.ceil(total / limit));

      setPettyCashList(list);
      setTotalItems(total);
      setTotalPages(pages);
    } catch (err) {
      errorNotify(err || "Failed to fetch petty cash entries");
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, searchText, searchFilters]);

  useEffect(() => {
    fetchPettyCash();
  }, [fetchPettyCash]);

  useEffect(() => {
    function handleClickOutside(event) {
      // If user clicked directly on a status badge trigger, let handleStatusClick handle the toggle
      if (
        event.target &&
        typeof event.target.closest === "function" &&
        event.target.closest(".petty_cash_status_trigger")
      ) {
        return;
      }

      if (
        statusPopupRef.current &&
        !statusPopupRef.current.contains(event.target)
      ) {
        setStatusPopup({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
      }
    }

    function handleScrollOrResize() {
      setStatusPopup((prev) => {
        if (!prev.open) return prev;
        return { open: false, rowId: null, anchorPos: { x: 0, y: 0 } };
      });
    }

    if (statusPopup.open) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside, { passive: true });
      window.addEventListener("scroll", handleScrollOrResize, true);
      window.addEventListener("resize", handleScrollOrResize);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [statusPopup.open]);

  const handleStatusClick = useCallback((event, row) => {
    event.stopPropagation();
    const normalizedStatus = String(row?.status || "").toLowerCase().trim();
    if (statusUpdatingId === row?.id) return;

    if (!canUpdatePettyCashStatus) {
      setStatusPopup({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
      setRestrictedStatusInfo({
        displayId:
          row?.trip?.trip_id ||
          (row?.trip_id ? `TR-${row.trip_id}` : `#${row?.id}`),
        currentStatus: row?.status || "Pending",
      });
      setPermissionModalOpen(true);
      return;
    }

    if (normalizedStatus !== "pending") return;

    const rect = event.currentTarget.getBoundingClientRect();
    setStatusPopup((prev) => {
      if (prev.open && prev.rowId === row.id) {
        return { open: false, rowId: null, anchorPos: { x: 0, y: 0 } };
      }
      return {
        open: true,
        rowId: row.id,
        anchorPos: {
          x: rect.right,
          y: rect.top,
          left: rect.left,
          right: rect.right,
          top: rect.top,
          bottom: rect.bottom,
        },
      };
    });
  }, [canUpdatePettyCashStatus, statusUpdatingId]);

  const handleStatusSelect = useCallback(async (status, row) => {
    if (!row?.id || statusUpdatingId) return;

    if (!canUpdatePettyCashStatus) {
      setStatusPopup({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
      setRestrictedStatusInfo({
        displayId:
          row?.trip?.trip_id ||
          (row?.trip_id ? `TR-${row.trip_id}` : `#${row?.id}`),
        currentStatus: row?.status || "Pending",
      });
      setPermissionModalOpen(true);
      return;
    }

    setStatusPopup({ open: false, rowId: null, anchorPos: { x: 0, y: 0 } });
    setStatusUpdatingId(row.id);
    try {
      await apiService.updatePettyCashStatus({
        petty_cash_id: row.id,
        status,
      });
      successNotify("Status updated successfully");
      fetchPettyCash();
    } catch (err) {
      errorNotify(err || "Failed to update petty cash status");
    } finally {
      setStatusUpdatingId(null);
    }
  }, [canUpdatePettyCashStatus, fetchPettyCash, statusUpdatingId]);

  const StatusPopup = useCallback(
    ({ row }) => {
      const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
      return (
        <TablePopover
          innerRef={statusPopupRef}
          anchorRect={statusPopup.anchorPos}
          popupHeight={96}
          popupWidth={144}
          align={isMobile ? "right" : "left"}
          offset={6}
          style={{
            padding: 0,
            background: "transparent",
            border: "none",
            boxShadow: "none",
          }}
        >
          <div className="petty_cash_status_popover_box">
            <div className="petty_cash_status_menu">
              <div className="petty_cash_status_menu_title">Update Status</div>
              <button
                type="button"
                className="petty_cash_status_option petty_cash_status_option_approved"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusSelect("approved", row);
                }}
              >
                <span className="petty_cash_status_option_icon">
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path
                      d="M1.25 4L3.75 6.5L8.75 1.5"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span>Approved</span>
              </button>
              <button
                type="button"
                className="petty_cash_status_option petty_cash_status_option_rejected"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusSelect("rejected", row);
                }}
              >
                <span className="petty_cash_status_option_icon">
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                    <path
                      d="M1.5 1.5L6.5 6.5M6.5 1.5L1.5 6.5"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span>Rejected</span>
              </button>
            </div>
          </div>
        </TablePopover>
      );
    },
    [handleStatusSelect, statusPopup.anchorPos],
  );

  const columns = useMemo(
    () => [
      {
        name: "Trip ID",
        selector: (row) => row?.trip?.trip_id || row?.trip_id || "-",
        cell: (row) => (
          <span className="hyperlink">
            {row?.trip?.trip_id || (row?.trip_id ? `TR-${row.trip_id}` : "-")}
          </span>
        ),
        minWidth: "180px",
        tableColumn: true,
        filterName: "tripIdFilter",
        type: "select",
        isMulti: true,
        filterComponent: TripIdFilterDropdown,
      },
      {
        name: "Vehicle",
        selector: (row) =>
          row?.vehicle?.registration_number || row?.vehicle_id || "-",
        cell: (row) => (
          <span>
            {row?.vehicle?.registration_number?.toUpperCase() ||
              (row?.vehicle_id ? `#${row.vehicle_id}` : "-")}
          </span>
        ),
        minWidth: "160px",
        tableColumn: true,
        filterName: "vehicleFilter",
        type: "select",
        isMulti: true,
        filterComponent: VehicleFilterDropdown,
        options: vehicleDropdownOptions,
      },
      // {
      //   name: "Status",
      //   selector: (row) => row?.status,
      //   cell: (row) => {
      //     const normalizedStatus = String(row?.status || "").toLowerCase().trim();
      //     const isPending = normalizedStatus === "pending";
      //     const isOpen = statusPopup.open && statusPopup.rowId === row.id;
      //     const isUpdating = statusUpdatingId === row?.id;
      //     return (
      //       <div className="petty_cash_status_trigger_wrapper">
      //         <span
      //           className={`petty_cash_status_trigger ${isPending || !canUpdatePettyCashStatus ? "is_clickable" : ""}`}
      //           onClick={(event) => handleStatusClick(event, row)}
      //         >
      //           {renderStatusBadge({
      //             statusValue: row?.status,
      //             canChangeStatus: isPending && canUpdatePettyCashStatus,
      //             isOpen,
      //             isUpdating,
      //           })}
      //         </span>
      //         {isOpen && <StatusPopup row={row} />}
      //       </div>
      //     );
      //   },
      //   minWidth: "140px",
      // },
      {
        name: "Amount",
        selector: (row) => row?.amount,
        cell: (row) => <span>{formatAmount(row?.amount)}</span>,
        minWidth: "140px",
      },
      {
        name: "Payment Mode",
        selector: (row) => row?.payment_mode || "-",
        cell: (row) => row?.payment_mode || "-",
        minWidth: "150px",
        tableColumn: true,
        filterName: "paymentModeFilter",
        type: "select",
        options: paymentModeOptions,
      },
      {
        name: "Source",
        selector: (row) => formatSource(row),
        cell: (row) => (
          <Tooltip title={formatSource(row)} placement="topLeft">
            <span className="truncate_cell_text">{formatSource(row)}</span>
          </Tooltip>
        ),
        minWidth: "230px",
      },
      {
        name: "Assign Date",
        selector: (row) => row?.assign_at,
        cell: (row) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {row?.assign_at ? formatDate(row.assign_at) : "-"}
          </span>
        ),
        minWidth: "210px",
        tableColumn: true,
        filterName: "assignAtFilter",
        type: "select",
        filterComponent: DateFilterDropdown,
      },
      {
        name: "Description",
        selector: (row) => row?.description || "-",
        cell: (row) => row?.description || "—",
        minWidth: "200px",
      },
    ],
    [
      vehicleDropdownOptions,
      paymentModeOptions,
      statusPopup,
      statusUpdatingId,
      canUpdatePettyCashStatus,
      handleStatusClick,
    ],
  );

  const mobileFilters = useMemo(
    () => [
      { name: "Trip ID", filterName: "tripIdFilter", type: "text" },
      {
        name: "Vehicle",
        filterName: "vehicleFilter",
        type: "select",
        options: vehicleDropdownOptions,
      },
      {
        name: "Payment Mode",
        filterName: "paymentModeFilter",
        type: "select",
        options: paymentModeOptions,
      },
      {
        name: "Assign Date",
        filterName: "assignAtFilter",
        filterComponent: DateFilterDropdown,
      },
    ],
    [paymentModeOptions, vehicleDropdownOptions],
  );

  return (
    <div className="trip_page petty_cash_page">

      {/* ── Mobile Header ── */}
      <div className="sub_page_mobile_header">
        <div className="sub_page_mobile_header_left">
          <button className="trips_mobile_menu_btn" onClick={toggleMobileSidebar}>
            <CgMenuLeft size={22} color="#1c1c1c" />
          </button>
          <h2 className="sub_page_mobile_title">Petty Cash</h2>
        </div>
      </div>

      {/* ── Mobile Export Row ── */}
      {(canExportPettyCashPdf || canExportPettyCashExcel) && (
        <div className="sub_page_mobile_export_row">
          {canExportPettyCashPdf && (
            <ExportButton
              type="pdf"
              onClick={handleExportPdf}
              disabled={!hasActiveFilters || pdfExportLoading}
              loading={pdfExportLoading}
              hasActiveFilters={hasActiveFilters}
            />
          )}
          {canExportPettyCashExcel && (
            <ExportButton
              type="excel"
              onClick={handleExportExcel}
              disabled={!hasActiveFilters || excelExportLoading}
              loading={excelExportLoading}
              hasActiveFilters={hasActiveFilters}
            />
          )}
        </div>
      )}

      <div className="desktop_only_el">
        <Breadcrumbs values={["Trip Management", "Petty Cash"]} />
      </div>

      <div className="access_view sub_page_desktop_header">
        <div className="d-flex align-items-center gap-3">
          <h2>Petty Cash</h2>
        </div>

        <div className="services_buttons">
          <Notification />

          {canExportPettyCashPdf && (
            <ExportButton
              type="pdf"
              onClick={handleExportPdf}
              disabled={!hasActiveFilters || pdfExportLoading}
              loading={pdfExportLoading}
              hasActiveFilters={hasActiveFilters}
            />
          )}

          {canExportPettyCashExcel && (
            <ExportButton
              type="excel"
              onClick={handleExportExcel}
              disabled={!hasActiveFilters || excelExportLoading}
              loading={excelExportLoading}
              hasActiveFilters={hasActiveFilters}
            />
          )}
        </div>
      </div>

      {/* ── Desktop Table ── */}
      <div className="desktop_table_view">
        <NewTableComponent
          showAddBtn={false}
          columns={columns}
          data={pettyCashList}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          limit={limit}
          setLimit={setLimit}
          totalItems={totalItems}
          totalPages={totalPages}
          onSearch={handleSearchChange}
          searchValue={searchText}
          loading={loading}
          paginationLoading={loading}
          setSearchFilters={updateSearchFilters}
          searchFilters={searchFilters}
          tableClassName="petty_cash_table"
        />
      </div>

      {/* ── Mobile Cards ── */}
      <div className="mobile_cards_view trips_list_container">
        <div className="mobile_search_bar_wrapper">
          <div className="mobile_search_input_box">
            <FiSearch className="search_icon_svg" />
            <input
              type="text"
              placeholder="Search petty cash..."
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
        </div>

        <MobileListingFilters
          filters={mobileFilters}
          searchFilters={searchFilters}
          setSearchFilters={updateSearchFilters}
        />

        {loading ? (
          <div className="mobile_cards_loading">Loading…</div>
        ) : pettyCashList.length === 0 ? (
          <div className="mobile_no_data">No petty cash entries found</div>
        ) : (
          pettyCashList.map((row) => {
            const normalizedStatus = String(row?.status || "").toLowerCase().trim();
            const isPending = normalizedStatus === "pending";
            return (
              <div key={row?.id} className="mobile_data_card">
                {/* Header: Trip ID + Status */}
                <div className="mobile_card_header">
                  <div className="mobile_card_id">
                    <span className="hyperlink">
                      {row?.trip?.trip_id || (row?.trip_id ? `TR-${row.trip_id}` : "—")}
                    </span>
                  </div>
                  {(() => {
                    const isOpen = statusPopup.open && statusPopup.rowId === row.id;
                    const isUpdating = statusUpdatingId === row?.id;
                    return (
                      <div className="petty_cash_status_trigger_wrapper">
                        <span
                          className={`petty_cash_status_trigger ${isPending || !canUpdatePettyCashStatus ? "is_clickable" : ""}`}
                          onClick={(event) => handleStatusClick(event, row)}
                        >
                          {renderStatusBadge({
                            statusValue: row?.status,
                            canChangeStatus: isPending && canUpdatePettyCashStatus,
                            isOpen,
                            isUpdating,
                          })}
                        </span>
                        {isOpen && <StatusPopup row={row} />}
                      </div>
                    );
                  })()}
                </div>

                {/* Vehicle + Amount */}
                <div className="mobile_card_row">
                  <div className="mobile_card_field">
                    <span className="mobile_card_label">Vehicle</span>
                    <span className="mobile_card_value">
                      {row?.vehicle?.registration_number?.toUpperCase() || (row?.vehicle_id ? `#${row.vehicle_id}` : "—")}
                    </span>
                  </div>
                  <div className="mobile_card_field">
                    <span className="mobile_card_label">Amount</span>
                    <span className="mobile_card_amount">{formatAmount(row?.amount)}</span>
                  </div>
                </div>

                {/* Payment Mode + Assign Date */}
                <div className="mobile_card_row">
                  <div className="mobile_card_field">
                    <span className="mobile_card_label">Payment Mode</span>
                    <span className="mobile_card_value">{row?.payment_mode || "—"}</span>
                  </div>
                  <div className="mobile_card_field">
                    <span className="mobile_card_label">Assign Date</span>
                    <span className="mobile_card_value">
                      {row?.assign_at ? formatDate(row.assign_at) : "—"}
                    </span>
                  </div>
                </div>

                {/* Source */}
                <div className="mobile_card_full_row">
                  <div className="mobile_card_field">
                    <span className="mobile_card_label">Source</span>
                    <span style={{ fontSize: "13px", color: "#1c1c1c", fontWeight: 500 }}>
                      {formatSource(row)}
                    </span>
                  </div>
                </div>

                {/* Description */}
                {row?.description && (
                  <div className="mobile_card_remarks">
                    <span className="mobile_card_label">Description</span>
                    <div style={{ marginTop: "2px" }}>{row.description}</div>
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* Mobile Pagination */}
        {totalPages > 1 && (
          <div className="mobile_pagination">
            <button
              className="mobile_page_btn"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >← Prev</button>
            <span className="mobile_page_info">Page {currentPage} of {totalPages}</span>
            <button
              className="mobile_page_btn"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >Next →</button>
          </div>
        )}
      </div>

      {/* Access Denied / Permission Required Modal */}
      <NoPermissionModal
        show={permissionModalOpen}
        onClose={() => setPermissionModalOpen(false)}
        title="Permission Required"
        message="You do not have permission to update the status of this petty cash record. Please contact your system administrator to request access."
        actionName="Status Update"
        permissionName="petty-cash-approval-status"
        tripId={restrictedStatusInfo?.displayId}
      />
    </div>
  );
};

export default PettyCash;
